// ** Redux Imports
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"

// ** Service Import
import navBarService from "../../../../services/Web/navBar.service"
import { toastError, toastSuccess } from "../../../../utility/toastutill"

// -------------------------------
// ✅ Async Thunks
// -------------------------------

// ** Get All NavBars (Hierarchical)
export const getAllNavBars = createAsyncThunk("navBars/getAll", async () => {
  try {
    const response = await navBarService.getNavBarsApi()
    return response.data.data?.hierarchy || []
  } catch (error) {
    toastError(error)
    throw error
  }
})

// ** Get NavBars with Pagination and Filtering
export const getNavBars = createAsyncThunk("navBars/get", async (params) => {
  let query = ""
  if (params) {
    const queryParams = []

    if (params.sort) queryParams.push(`sort=${params.sort}`)
    if (params.sortColumn) queryParams.push(`sortColumn=${params.sortColumn}`)
    if (params.q) queryParams.push(`q=${params.q}`)
    if (params.perPage) queryParams.push(`perPage=${params.perPage}`)
    if (params.page) queryParams.push(`page=${params.page}`)
    if (params.status) queryParams.push(`status=${params.status}`)
    if (params.is_active !== undefined) {
      queryParams.push(`is_active=${params.is_active}`)
    }

    query = queryParams.join("&")
  }

  try {
    const response = await navBarService.getNavBars(query)
    return {
      params,
      data: response.data.data?.list || [],
      totalPages: response.data.data?.total || 0
    }
  } catch (error) {
    toastError(error)
    throw error
  }
})

// ** Get Single NavBar by ID
export const getNavBarById = createAsyncThunk("navBars/getById", async (id) => {
  try {
    const response = await navBarService.getNavBarByIdApi(id)
    if (response.data.success) toastSuccess(response.data.message)
    return response.data.data
  } catch (error) {
    toastError(error)
    throw error
  }
})

// ** Add New NavBar
export const addNavBar = createAsyncThunk(
  "navBars/add",
  async (formData, { dispatch, getState }) => {
    try {
      const response = await navBarService.addNavBarApi(formData)
      if (response.data.success) {
        toastSuccess(response.data.message)
        // Refresh both lists
        await dispatch(getAllNavBars())
        const params = getState().navBars.params
        if (Object.keys(params).length > 0) {
          await dispatch(getNavBars(params))
        }
      }
      return response.data
    } catch (error) {
      toastError(error)
      throw error
    }
  }
)

// ** Update NavBar
export const updateNavBar = createAsyncThunk(
  "navBars/update",
  async ({ id, ...formData }, { dispatch, getState }) => {
    try {
      const response = await navBarService.updateNavBarApi(id, formData)
      if (response.data.success) {
        toastSuccess(response.data.message)
        // Refresh both lists
        await dispatch(getAllNavBars())
        const params = getState().navBars.params
        if (Object.keys(params).length > 0) {
          await dispatch(getNavBars(params))
        }
      }
      return response.data
    } catch (error) {
      toastError(error)
      throw error
    }
  }
)

// ** Delete NavBar
export const deleteNavBar = createAsyncThunk(
  "navBars/delete",
  async (id, { dispatch, getState }) => {
    try {
      const response = await navBarService.deleteNavBarApi(id)
      if (response.data.success) {
        toastSuccess(response.data.message)
        // Refresh both lists
        await dispatch(getAllNavBars())
        const params = getState().navBars.params
        if (Object.keys(params).length > 0) {
          await dispatch(getNavBars(params))
        }
      }
      return id
    } catch (error) {
      toastError(error)
      throw error
    }
  }
)

// ** Reorder Nav Items
export const reorderNavItems = createAsyncThunk(
  "navBars/reorder",
  async (formData, { dispatch, getState }) => {
    try {
      console.log("Dispatching reorder with payload:", formData)
      const response = await navBarService.reorderNavItemsApi(formData)

      if (response.data.success) {
        toastSuccess(response.data.message)
        // Refresh hierarchy immediately
        await dispatch(getAllNavBars())
      }
      return response.data
    } catch (error) {
      toastError(error)
      throw error
    }
  }
)

// ** Convert to SubItem
export const convertToSubItem = createAsyncThunk(
  "navBars/convertSubItem",
  async (formData, { dispatch, getState }) => {
    try {
      console.log("Dispatching convertToSubItem with payload:", formData)
      const response = await navBarService.convertToSubItemApi(formData)

      if (response.data.success) {
        toastSuccess(response.data.message)
        // Refresh hierarchy immediately
        await dispatch(getAllNavBars())
      }
      return response.data
    } catch (error) {
      toastError(error)
      throw error
    }
  }
)

// -------------------------------
// ✅ Slice Definition
// -------------------------------
export const navBarsSlice = createSlice({
  name: "navBars",
  initialState: {
    data: [],
    total: 1,
    params: {},
    allData: [],
    selectedNavBar: null,
    success: false,
    loading: false,
    error: null
  },
  reducers: {
    clearSelectedNavBar: (state) => {
      state.selectedNavBar = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearSuccess: (state) => {
      state.success = false
    }
  },
  extraReducers: (builder) => {
    builder
      // Get All (Hierarchy)
      .addCase(getAllNavBars.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getAllNavBars.fulfilled, (state, action) => {
        state.loading = false
        state.allData = action.payload || []
        state.error = null
      })
      .addCase(getAllNavBars.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Get Paginated (List)
      .addCase(getNavBars.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getNavBars.fulfilled, (state, action) => {
        state.loading = false
        state.data = action.payload.data || []
        state.params = action.payload.params || {}
        state.total = action.payload.totalPages || 1
        state.error = null
      })
      .addCase(getNavBars.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Get by ID
      .addCase(getNavBarById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getNavBarById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedNavBar = action.payload
        state.error = null
      })
      .addCase(getNavBarById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Add
      .addCase(addNavBar.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(addNavBar.fulfilled, (state) => {
        state.loading = false
        state.success = true
        state.error = null
      })
      .addCase(addNavBar.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.error.message
      })

      // Update
      .addCase(updateNavBar.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(updateNavBar.fulfilled, (state) => {
        state.loading = false
        state.success = true
        state.selectedNavBar = null
        state.error = null
      })
      .addCase(updateNavBar.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.error.message
      })

      // Delete
      .addCase(deleteNavBar.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteNavBar.fulfilled, (state, action) => {
        state.loading = false
        state.data = state.data.filter((i) => i._id !== action.payload)
        state.allData = state.allData.filter((i) => i._id !== action.payload)
        state.error = null
      })
      .addCase(deleteNavBar.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      // Reorder
      .addCase(reorderNavItems.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(reorderNavItems.fulfilled, (state) => {
        state.loading = false
        state.success = true
        state.error = null
      })
      .addCase(reorderNavItems.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.error.message
      })

      // Convert to SubItem
      .addCase(convertToSubItem.pending, (state) => {
        state.loading = true
        state.success = false
        state.error = null
      })
      .addCase(convertToSubItem.fulfilled, (state) => {
        state.loading = false
        state.success = true
        state.error = null
      })
      .addCase(convertToSubItem.rejected, (state, action) => {
        state.loading = false
        state.success = false
        state.error = action.error.message
      })
  }
})

export const { clearSelectedNavBar, clearError, clearSuccess } =
  navBarsSlice.actions
export default navBarsSlice.reducer
